import {
  GraphQLList,
  GraphQLNonNull,
  GraphQLScalarType,
  GraphQLEnumType,
  GraphQLObjectType,
  GraphQLInputType,
  GraphQLUnionType,
} from "graphql";
import { getOperationDefinition } from "./operationUtils";

interface ExecuteResult {
  index: string;
  success: boolean;
  _index?: string;
  [key: string]: any;
}

export async function executeRows(
  endpoint: string,
  token: string,
  schemaSDL: string,
  operationName: string,
  rows: any[],
  onProgress: (done: number) => void,
  selectionPaths?: string[],
  operationType: "mutation" | "query" = "mutation",
): Promise<ExecuteResult[]> {
  const results: ExecuteResult[] = [];
  // An empty selection means "nothing chosen", not "select nothing": fall back
  // to the schema-derived default so the response is never silently blank.
  const selection = selectionPaths?.length ? selectionPaths : undefined;
  const query = buildOperation(
    schemaSDL,
    operationName,
    selection,
    operationType,
  );
  let done = 0;

  for (const [idx, vars] of rows.entries()) {
    try {
      const resp = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query, variables: vars }),
      });
      const json = await resp.json();
      const data = json.data?.[operationName];
      if (!data) {
        results.push({
          index: String(idx),
          success: false,
          error: JSON.stringify(json.errors ?? json),
        });
      } else {
        let rows: Record<string, any>[] = [];
        if (Array.isArray(data)) {
          if (selection) {
            rows = pickSelectedRows(data, selection);
          } else {
            rows = data.map((item, i) => ({
              _index: String(i),
              ...pickScalars(item),
            }));
          }
        } else {
          rows = selection
            ? pickSelectedRows(data, selection)
            : [pickScalars(data)];
        }
        const errorAt = (row: Record<string, any>): string | undefined =>
          exceptionError(Array.isArray(data) ? data[Number(row._index)] : data);
        if (rows.length === 0) {
          results.push({ index: String(idx), success: true });
        } else {
          rows.forEach((row) => {
            const error = errorAt(row);
            results.push({
              index: String(idx),
              success: error === undefined,
              ...(error === undefined ? {} : { error }),
              ...row,
            });
          });
        }
      }
    } catch (e: any) {
      results.push({
        index: String(idx),
        success: false,
        error: String(e),
      });
    }
    done += 1;
    onProgress(done);
  }

  return results;
}

function buildOperation(
  schemaSDL: string,
  name: string,
  selectionPaths?: string[],
  type: "mutation" | "query" = "mutation",
): string {
  const field = getOperationDefinition(schemaSDL, type, name);
  const varDefs: string[] = [];
  const argStrings: string[] = [];
  field.args.forEach((arg) => {
    varDefs.push(`$${arg.name}: ${printType(arg.type)}`);
    argStrings.push(`${arg.name}: $${arg.name}`);
  });
  const selection = selectionPaths
    ? buildSelectionFromPaths(field.type, selectionPaths)
    : buildSelection(field.type);
  const selStr = selection ? ` ${selection}` : "";
  const opKeyword = type === "mutation" ? "mutation" : "query";
  const varStr = varDefs.length ? `(${varDefs.join(", ")})` : "";
  const argStr = argStrings.length ? `(${argStrings.join(", ")})` : "";
  return `${opKeyword} BATCH_OPERATION${varStr} { ${name}${argStr}${selStr} }`;
}

function printType(type: GraphQLInputType): string {
  if (type instanceof GraphQLNonNull) return `${printType(type.ofType)}!`;
  if (type instanceof GraphQLList) return `[${printType(type.ofType)}]`;
  return (type as any).name;
}

function buildSelection(rtype: any): string {
  while (rtype instanceof GraphQLNonNull || rtype instanceof GraphQLList)
    rtype = rtype.ofType;
  if (rtype instanceof GraphQLUnionType) {
    const types = rtype.getTypes();
    const fragments: string[] = ["__typename"];
    for (const type of types) {
      const fields = type.getFields();
      const keys = Object.keys(fields).filter((k) => {
        let t: any = fields[k].type;
        while (t instanceof GraphQLNonNull || t instanceof GraphQLList)
          t = t.ofType;
        return t instanceof GraphQLScalarType || t instanceof GraphQLEnumType;
      });
      if (keys.length > 0) {
        fragments.push(`... on ${type.name} { ${keys.join(" ")} }`);
      }
    }
    return `{ ${fragments.join(" ")} }`;
  }
  if (rtype instanceof GraphQLObjectType) {
    const fields = rtype.getFields();
    const keys = Object.keys(fields).filter((k) => {
      let t: any = fields[k].type;
      while (t instanceof GraphQLNonNull || t instanceof GraphQLList)
        t = t.ofType;
      return t instanceof GraphQLScalarType || t instanceof GraphQLEnumType;
    });
    if (keys.length === 0) return "";
    return `{ ${keys.join(" ")} }`;
  }
  return "";
}

/** A Union member whose type name ends with "Exception" is the schema's way of
 *  reporting a rejected operation (e.g. SaveJsProjectResult = JsProject |
 *  GenericException). GraphQL reports no `errors` for those, so the failure is
 *  only visible through __typename. Returns the exception's scalar fields as an
 *  error message, or undefined when the response is not an exception. */
function exceptionError(item: any): string | undefined {
  if (!item || typeof item !== "object" || Array.isArray(item))
    return undefined;
  const typename = item.__typename;
  if (typeof typename !== "string" || !typename.endsWith("Exception"))
    return undefined;
  return JSON.stringify(pickScalars(item));
}

function pickScalars(obj: any): Record<string, any> {
  if (obj == null) return { value: null };
  if (typeof obj !== "object" || Array.isArray(obj)) return { value: obj };
  const out: Record<string, any> = {};
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (val === null) {
      out[key] = null;
    } else if (typeof val !== "object") {
      out[key] = val;
    }
  }
  return out;
}

function pickSelectedFields(obj: any, paths: string[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const p of paths) {
    const parts = p.split(".");
    const val = getPath(obj, parts);
    out[p] = val === undefined ? null : val;
  }
  return out;
}

function pickSelectedRows(obj: any, paths: string[]): Record<string, any>[] {
  const tree = buildTreeFromPaths(paths);

  // Handle root-level Union type responses
  if (paths.some((p) => p.startsWith("["))) {
    const typeName = obj?.__typename;
    const result: Record<string, any> = { __typename: typeName ?? null };

    for (const p of paths) {
      const match = p.match(/^\[([^\]]+)\](?:\.(.+))?$/);
      if (match) {
        const [, unionTypeName, fieldPath] = match;
        if (typeName === unionTypeName && fieldPath) {
          const parts = fieldPath.split(".");
          result[`[${unionTypeName}].${fieldPath}`] = getPath(obj, parts);
        } else if (fieldPath) {
          result[`[${unionTypeName}].${fieldPath}`] = null;
        }
      }
    }
    return [result];
  }

  if (Array.isArray(obj)) {
    const rows: Record<string, any>[] = [];
    for (let i = 0; i < obj.length; i++) {
      let childRows = flattenSelected(obj[i], tree);
      if (childRows.length === 0) childRows = [{}];
      childRows = childRows.map((r) => ({ _index: String(i), ...r }));
      rows.push(...childRows);
    }
    return rows;
  }
  return flattenSelected(obj, tree);
}

function buildTreeFromPaths(paths: string[]): Record<string, any> {
  const tree: Record<string, any> = {};

  // Handle root-level Union type paths
  if (paths.some((p) => p.startsWith("["))) {
    tree.__typename = {};
    paths.forEach((p) => {
      const match = p.match(/^\[([^\]]+)\](?:\.(.+))?$/);
      if (match) {
        const [, typeName, restPath] = match;
        if (!tree[typeName]) tree[typeName] = {};
        if (restPath) {
          const parts = restPath.split(".");
          let node = tree[typeName];
          parts.forEach((part) => {
            if (!node[part]) node[part] = {};
            node = node[part];
          });
        }
      }
    });
    return tree;
  }

  paths.forEach((p) => {
    // Handle nested union notation like "field[TypeName].subfield"
    const parts: string[] = [];
    let current = "";
    let inBrackets = false;

    for (let i = 0; i < p.length; i++) {
      const char = p[i];
      if (char === "[" && !inBrackets) {
        inBrackets = true;
        current += char;
      } else if (char === "]" && inBrackets) {
        inBrackets = false;
        current += char;
      } else if (char === "." && !inBrackets) {
        if (current) {
          parts.push(current);
          current = "";
        }
      } else {
        current += char;
      }
    }
    if (current) parts.push(current);

    let node = tree;
    parts.forEach((part) => {
      if (!node[part]) node[part] = {};
      node = node[part];
    });
  });
  return tree;
}

function flattenSelected(
  obj: any,
  tree: Record<string, any>,
  prefix = "",
): Record<string, any>[] {
  if (obj == null) {
    const base: Record<string, any> = {};
    collectNullLeaves(tree, prefix, base);
    return [base];
  }

  // Handle Union type responses
  if (tree.__typename && obj.__typename) {
    const typeName = obj.__typename;
    const unionTree = tree[typeName];
    if (unionTree) {
      const result: Record<string, any> = {};
      result[`${prefix}__typename`] = typeName;

      // Process fields for the matched type
      for (const key of Object.keys(unionTree)) {
        const sub = unionTree[key];
        const val = obj[key];
        const fieldKey = `${prefix}[${typeName}].${key}`;

        if (val == null || Object.keys(sub).length === 0) {
          result[fieldKey] = val === undefined ? null : val;
        } else if (typeof val === "object" && !Array.isArray(val)) {
          const childRows = flattenSelected(val, sub, `${fieldKey}.`);
          Object.assign(result, childRows[0] || {});
        } else {
          result[fieldKey] = val;
        }
      }

      // Add null values for other types
      for (const otherType of Object.keys(tree)) {
        if (otherType !== "__typename" && otherType !== typeName) {
          collectNullLeaves(
            tree[otherType],
            `${prefix}[${otherType}].`,
            result,
          );
        }
      }

      return [result];
    }
  }

  const scalars: Record<string, any> = {};
  const childRowsList: Record<string, any>[][] = [];

  for (const key of Object.keys(tree)) {
    // Handle Union field notation like "field[TypeName]"
    const unionMatch = key.match(/^([^[]+)\[([^\]]+)\]$/);
    if (unionMatch) {
      const [, fieldName, typeName] = unionMatch;
      const fieldVal = obj[fieldName];
      if (fieldVal && fieldVal.__typename === typeName) {
        const sub = tree[key];
        for (const subKey of Object.keys(sub)) {
          const val = fieldVal[subKey];
          scalars[`${prefix}${key}.${subKey}`] = val === undefined ? null : val;
        }
      } else {
        // Add null values for this union type
        collectNullLeaves(tree[key], `${prefix}${key}.`, scalars);
      }
      continue;
    }

    const sub = tree[key];
    const val = obj[key];
    if (
      val != null &&
      typeof val === "object" &&
      !Array.isArray(val) &&
      Object.keys(sub).length
    ) {
      const childRows = flattenSelected(val, sub, `${prefix}${key}.`);
      childRowsList.push(childRows);
    } else if (Array.isArray(val)) {
      const arrRows: Record<string, any>[] = [];
      for (let i = 0; i < val.length; i++) {
        let childRows = flattenSelected(val[i], sub, `${prefix}${key}.`);
        if (childRows.length === 0) childRows = [{}];
        childRows = childRows.map((r) => ({
          [`${prefix}${key}._index`]: String(i),
          ...r,
        }));
        arrRows.push(...childRows);
      }
      childRowsList.push(arrRows);
    } else if (val == null && Object.keys(sub).length) {
      const nullBase: Record<string, any> = {};
      collectNullLeaves(sub, `${prefix}${key}.`, nullBase);
      childRowsList.push([nullBase]);
    } else {
      scalars[`${prefix}${key}`] = val === undefined ? null : val;
    }
  }

  let rows: Record<string, any>[] = [{}];
  for (const childRows of childRowsList) {
    rows = mergeRows(rows, childRows);
  }

  return rows.map((r) => ({ ...scalars, ...r }));
}

function collectNullLeaves(
  tree: Record<string, any>,
  prefix: string,
  out: Record<string, any>,
): void {
  for (const key of Object.keys(tree)) {
    const sub = tree[key];
    if (Object.keys(sub).length === 0) out[`${prefix}${key}`] = null;
    else collectNullLeaves(sub, `${prefix}${key}.`, out);
  }
}

function mergeRows(
  rows1: Record<string, any>[],
  rows2: Record<string, any>[],
): Record<string, any>[] {
  const count = Math.max(rows1.length, rows2.length);
  const out: Record<string, any>[] = [];
  for (let i = 0; i < count; i++) {
    out.push({ ...(rows1[i] ?? {}), ...(rows2[i] ?? {}) });
  }
  return out;
}

function getPath(obj: any, parts: string[]): any {
  let cur = obj;
  for (let i = 0; i < parts.length; i++) {
    if (cur == null) return undefined;
    if (Array.isArray(cur)) {
      return cur.map((item) => getPath(item, parts.slice(i)));
    }
    cur = cur[parts[i]];
  }
  return cur;
}

function buildSelectionFromPaths(rtype: any, paths: string[]): string {
  const tree: Record<string, any> = {};

  // Check if all paths start with [TypeName] (root level Union)
  const isRootUnion = paths.every((p) => p.startsWith("["));

  if (isRootUnion) {
    // Handle root-level Union type
    tree.__typename = {};
    paths.forEach((p) => {
      const match = p.match(/^\[([^\]]+)\](?:\.(.+))?$/);
      if (match) {
        const [, typeName, restPath] = match;
        if (!tree[typeName]) tree[typeName] = {};
        if (restPath) {
          const parts = restPath.split(".");
          let node = tree[typeName];
          parts.forEach((part) => {
            if (!node[part]) node[part] = {};
            node = node[part];
          });
        }
      }
    });
    return buildSelectionFromTree(rtype, tree);
  }

  paths.forEach((p) => {
    // Parse paths like "field[TypeName].subfield" or "field.subfield"
    const parts: string[] = [];
    let current = "";
    let inBrackets = false;

    for (let i = 0; i < p.length; i++) {
      const char = p[i];
      if (char === "[" && !inBrackets) {
        inBrackets = true;
        current += char;
      } else if (char === "]" && inBrackets) {
        inBrackets = false;
        current += char;
      } else if (char === "." && !inBrackets) {
        if (current) {
          parts.push(current);
          current = "";
        }
      } else {
        current += char;
      }
    }
    if (current) parts.push(current);

    // Build tree from parts
    let node = tree;
    parts.forEach((part) => {
      if (!node[part]) node[part] = {};
      node = node[part];
    });
  });

  return buildSelectionFromTree(rtype, tree);
}

function buildSelectionFromUnionTree(type: any, tree: any): string {
  while (type instanceof GraphQLNonNull || type instanceof GraphQLList)
    type = type.ofType;
  if (!(type instanceof GraphQLUnionType)) return "";

  const parts: string[] = [];
  if (tree.__typename) parts.push("__typename");

  const types = type.getTypes();
  for (const unionType of types) {
    if (tree[unionType.name]) {
      const fields = unionType.getFields();
      const fragmentFields: string[] = [];
      for (const key of Object.keys(tree[unionType.name])) {
        if (!fields[key]) continue;
        const sub = buildSelectionFromTree(
          fields[key].type,
          tree[unionType.name][key],
        );
        if (sub) fragmentFields.push(`${key} ${sub}`);
        else fragmentFields.push(key);
      }
      if (fragmentFields.length > 0) {
        parts.push(`... on ${unionType.name} { ${fragmentFields.join(" ")} }`);
      }
    }
  }

  if (parts.length === 0) return "";
  return `{ ${parts.join(" ")} }`;
}

function buildSelectionFromTree(type: any, tree: any): string {
  while (type instanceof GraphQLNonNull || type instanceof GraphQLList)
    type = type.ofType;
  if (type instanceof GraphQLUnionType) {
    return buildSelectionFromUnionTree(type, tree);
  }
  if (!(type instanceof GraphQLObjectType)) return "";
  const fields = type.getFields();
  const parts: string[] = [];
  for (const key of Object.keys(tree)) {
    // Check if key has Union type notation like "field[TypeName]"
    const unionMatch = key.match(/^([^[]+)\[([^\]]+)\]$/);
    if (unionMatch) {
      const [, fieldName, typeName] = unionMatch;
      if (!fields[fieldName]) continue;

      // Build union tree for this field
      const unionTree: Record<string, any> = { __typename: {} };
      unionTree[typeName] = tree[key];

      // Check for other union types on the same field
      for (const otherKey of Object.keys(tree)) {
        const otherMatch = otherKey.match(/^([^[]+)\[([^\]]+)\]$/);
        if (
          otherMatch &&
          otherMatch[1] === fieldName &&
          otherMatch[2] !== typeName
        ) {
          unionTree[otherMatch[2]] = tree[otherKey];
        }
      }

      const sub = buildSelectionFromTree(fields[fieldName].type, unionTree);
      if (sub && !parts.some((p) => p.startsWith(fieldName + " "))) {
        parts.push(`${fieldName} ${sub}`);
      }
    } else if (!key.includes("[")) {
      // Regular field
      if (!fields[key]) continue;
      const sub = buildSelectionFromTree(fields[key].type, tree[key]);
      if (sub) parts.push(`${key} ${sub}`);
      else parts.push(key);
    }
  }
  if (parts.length === 0) return "";
  return `{ ${parts.join(" ")} }`;
}
